<template>
  <q-dialog
    v-model="dialogState"
    :position="position"
    @before-show="emits('before-show')"
    @hide="localOnHide"
    @show="emits('show')"
    maximized
    @before-hide="emits('before-hide')"
    role="alertdialog"
    :aria-labelledby="props?.title ? props.title : 'TITLE'"
    :aria-describedby="props?.title ? props.title : 'Description'"
  >
    <q-card :class="`q-card-${props.size}`">
      <!-- Dialog Title -->
      <q-card-section class="dialogTitle" v-if="props.title">
        <q-btn
          class="btnMenu"
          unelevated
          flat
          round
          dense
          text-color="white"
          @click="emits('applyBack')"
          v-if="btnBack && !searchInput"
          aria-label="Back"
        >
          <SvgIcon size="1.2rem" icon="back" />
        </q-btn>
        <q-btn
          flat
          icon="check"
          class="no-hover q-mr-lg"
          v-close-popup
          v-if="btnCheck"
          dense
          @click="emits('applyFilter')"
          aria-label="Filter"
        />
        <div class="dialogTitleHeading" v-if="!searchInput">
          <i
            v-if="props.icon"
            aria-hidden="true"
            role="presentation"
            class="material-icons q-icon notranslate q-mr-sm"
            style="font-size: 20px"
          >
            {{ props.icon }}
          </i>
          {{ props.title }}
        </div>

        <q-input
          clearable
          borderded
          class="ftx-dialog-search"
          dense
          outlined
          clear-icon="close"
          v-if="searchInput"
          v-model="searchText"
          @keypress.enter="emits('search', { searchText })"
          :autofocus="true"
          @clear="emits('search', { searchText })"
        />
        <q-btn
          flat
          icon="search"
          class="absolute-right no-hover"
          v-if="btnSearch && !searchInput"
          @click="!searchText ? (searchInput = !searchInput) : null"
          aria-label="Search"
        />
        <q-btn
          flat
          icon="close"
          class="absolute-right no-hover"
          v-if="btnSearch && searchInput"
          @click="!searchText ? (searchInput = !searchInput) : null"
          aria-label="Close"
        />

        <q-btn
          flat
          icon="close"
          class="absolute-right no-hover"
          v-close-popup
          v-if="btnClose"
          aria-label="Close"
        />
      </q-card-section>
      <!-- Dialog Title -->

      <!-- Simple No scroll No form Content -->
      <q-card-section class="dialogContent q-pa-none" v-if="hasSimpleContent">
        <slot name="SimpleContent" />
      </q-card-section>
      <!-- Simple No scroll No form Content -->

      <!-- Form,Sroll, footer Content -->
      <q-form @submit="emits('submit')" v-if="hasScrollContent">
        <!-- <q-scroll-area
          :thumb-style="commonMixinConstants.scrollAreaThumbStyle"
          :bar-style="commonMixinConstants.scrollAreaBarStyle"
          :visible="commonMixinConstants.scrollAreaVisible"
          class="ContentScrollArea"
          :style="{
            height: scrollHeight,
          }"
        ></q-scroll-area> -->
        <q-card-section class="dialogContent">
          <slot name="ScrollContent" />
        </q-card-section>

        <q-card-section class="dialogBottom" v-if="showFooter">
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-12 text-right">
              <slot name="FooterButtons" v-if="hasFooterButtons" />
              <template v-else>
                <q-btn
                  unelevated
                  no-caps
                  color="blue-grey-1"
                  text-color="blue-grey-8"
                  label="Cancel"
                  class="q-mr-md"
                  size="13px"
                  padding="8px 24px"
                  v-close-popup
                />
                <q-btn
                  unelevated
                  no-caps
                  color="primary"
                  label="Submit"
                  size="13px"
                  padding="8px 24px"
                  type="submit"
                />
              </template>
            </div>
          </div>
        </q-card-section>
      </q-form>
      <!-- Form,Sroll, footer Content -->

      <slot name="AdditionalContent" />
      <slot name="CustomContent" />
    </q-card>
  </q-dialog>
</template>

<script setup>
import { computed, useSlots, ref } from "vue";
import { useDialogStore } from "src/stores/dialog-store";
import {
  updateDialogState,
  deleteDialogProperties,
} from "src/utils/dialogHelper";
// import { commonMixinConstants } from "/src/utils/constants";

// emits
const emits = defineEmits([
  "submit",
  "before-show",
  "before-hide",
  "hide",
  "show",
  "applyBack",
  "search",
  "applyFilter",
]);

// emits
const props = defineProps({
  dialog: String,
  position: String,
  size: String,
  title: String,
  icon: String,
  showFooter: Boolean,
  btnClose: {
    type: Boolean,
    default: true,
  },
  btnBack: {
    type: Boolean,
    default: false,
  },
  btnSearch: {
    type: Boolean,
    default: false,
  },
  btnCheck: {
    type: Boolean,
    default: false,
  },
});

const dialogStore = useDialogStore();

const slots = useSlots();

const searchText = ref("");
const searchInput = ref(false);

// Computed
const hasSimpleContent = computed(() => {
    return !!slots.hasOwnProperty("SimpleContent");
  }),
  hasFooterButtons = computed(() => {
    return !!slots.hasOwnProperty("FooterButtons");
  }),
  hasScrollContent = computed(() => {
    return !!slots.hasOwnProperty("ScrollContent");
  }),
  scrollHeight = computed(() => {
    let removeHeight = 76;

    if (props.showFooter) removeHeight += 70;

    return `calc(80vh - ${removeHeight}px)`;
  }),
  dialogState = computed({
    get() {
      return dialogStore.getDialogState(props.dialog);
    },
    set(val) {
      updateDialogState({
        dialog: props.dialog,
        val,
      });
    },
  });

// methods
const localOnHide = () => {
  deleteDialogProperties(props.dialog);
  emits("hide");
};
</script>

<style lang="scss">
.q-dialog .q-dialog__inner--bottom {
  bottom: env(safe-area-inset-bottom) !important;
}
</style>
